import { createBrowserRouter, redirect } from 'react-router-dom';
import { msalInstance } from './auth/msalConfig';
import { Login } from './routes/Login';
import { AuthCallback } from './routes/AuthCallback';
import { ProtectedLayout } from './routes/ProtectedLayout';
import { MyTickets } from './routes/MyTickets';
import { Queue } from './routes/Queue';
import { NewTicket } from './routes/NewTicket';
import { TicketDetail } from './routes/TicketDetail';
import { AdminApplications } from './routes/AdminApplications';
import { AdminIgnoredSenders } from './routes/AdminIgnoredSenders';
import { Reports } from './routes/Reports';
import { AdminSlaTargets } from './routes/AdminSlaTargets';

/**
 * Central router config (React Router v7 data router).
 *
 * Auth gating lives in the protected layout's `loader`: before any child
 * route renders, we check MSAL for an active account and redirect to
 * /login with the original URL captured in ?redirect= so we can bounce
 * the user back after sign-in.
 *
 * As the app grows, prefer lazy route modules (`lazy: () => import(...)`)
 * over direct imports — the data router supports them natively and they
 * give us route-level code splitting for free.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    loader: () => {
      const account = msalInstance.getActiveAccount();
      return redirect(account ? '/tickets' : '/login');
    }
  },
  { path: '/login', element: <Login /> },
  { path: '/auth/callback', element: <AuthCallback /> },
  {
    id: 'protected',
    element: <ProtectedLayout />,
    loader: ({ request }) => {
      const account = msalInstance.getActiveAccount();
      if (!account) {
        const url = new URL(request.url);
        const target = url.pathname + url.search;
        return redirect(`/login?redirect=${encodeURIComponent(target)}`);
      }
      return null;
    },
    children: [
      { path: '/tickets', element: <MyTickets /> },
      { path: '/tickets/:ticketNumber', element: <TicketDetail /> },
      { path: '/queue', element: <Queue /> },
      { path: '/new-ticket', element: <NewTicket /> },
      { path: '/settings', loader: () => redirect('/settings/applications') },
      { path: '/settings/applications', element: <AdminApplications /> },
      { path: '/settings/ignored-senders', element: <AdminIgnoredSenders /> },
      { path: '/settings/reports', element: <Reports /> },
      { path: '/settings/sla-targets', element: <AdminSlaTargets /> },
      // Back-compat for the old path.
      { path: '/admin/applications', loader: () => redirect('/settings/applications') }
    ]
  }
]);
